// Persistent "pin" (aka star) support for Fusion combinations.
// Pins survive page refresh and full data reloads: they are stored in
// localStorage, keyed by a stable fingerprint derived from the underlying
// structural properties of the combination (expirations + strikes), so they
// keep matching up even when the backend reassigns `id` values after a rescan.

export const PIN_STORAGE_KEY = 'fusionPins_v1';

const normExp = (e) => {
  if (e == null || e === '') return '';
  if (e instanceof Date && !Number.isNaN(e.getTime())) return e.toISOString().slice(0, 10);
  const s = String(e).trim();
  return s.length >= 10 ? s.slice(0, 10) : s;
};

const normStrike = (s) => {
  if (s == null || s === '') return '';
  const n = Number(s);
  return Number.isFinite(n) ? String(n) : String(s).trim();
};

const normQty = (q) => {
  const n = Number(q);
  return Number.isFinite(n) && n > 0 ? String(n) : '1';
};

// Stable fingerprint for a combination. Independent of `id` which the
// backend regenerates on every scan.
export const makePinKey = (row) => {
  if (!row) return '';
  const cal = row.calendar_spread?.legs || [];
  const diag = row.diagonal_spread?.legs || [];
  const vert = row.vertical_spread?.legs || [];
  const frontExp = normExp(cal[0]?.expiration);
  const backExp = normExp(cal[1]?.expiration);
  const calStrike = normStrike(cal[0]?.strike);
  const diagShort = normStrike(diag[0]?.strike);
  const diagLong = normStrike(diag[1]?.strike);
  const vertShort = normStrike(vert[0]?.strike);
  const vertLong = normStrike(vert[1]?.strike);
  const calQty = normQty(row.calendar_quantity);
  const diagQty = normQty(row.diagonal_quantity);
  const vertQty = normQty(row.vertical_quantity);
  return `${frontExp}|${backExp}|c:${calStrike}x${calQty}|d:${diagShort}/${diagLong}x${diagQty}|v:${vertShort}/${vertLong}x${vertQty}`;
};

const EMPTY_STORE = { front: {}, back: {} };

const rekeyBucket = (bucket) => {
  if (!bucket || typeof bucket !== 'object') return {};
  const out = {};
  for (const [, data] of Object.entries(bucket)) {
    const snap = data?.snapshot;
    if (snap && typeof snap === 'object') {
      const k = makePinKey(snap);
      if (k) out[k] = data;
    }
  }
  return out;
};

/** Rebuild keys from snapshots so date/strike normalization stays consistent. */
export const readAllPins = () => {
  try {
    const raw = localStorage.getItem(PIN_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      const frontIn = parsed?.front && typeof parsed.front === 'object' ? parsed.front : {};
      const backIn = parsed?.back && typeof parsed.back === 'object' ? parsed.back : {};
      return {
        front: rekeyBucket(frontIn),
        back: rekeyBucket(backIn),
      };
    }
  } catch (_) {
    return { ...EMPTY_STORE };
  }
  return { ...EMPTY_STORE };
};

export const writeAllPins = (all) => {
  try {
    localStorage.setItem(PIN_STORAGE_KEY, JSON.stringify(all));
  } catch (_) {}
};
